import { TextField, useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconGripVertical } from '@calendly/ui/components/glyphs/IconGripVertical'
import { IconPlus } from '@calendly/ui/components/glyphs/IconPlus'
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash'
import { IconButton } from '@calendly/ui/components/icon-button'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { colorIconSecondary, colorIconStandard } from '@calendly/ui/theme'
import { nanoid } from 'nanoid'
import { useEffect } from 'react'

import ErrorMessage from 'components/core/error_message'
import { useAdvancedSortable, VerticalSortableContext } from 'components/core/sortable'
import { useErrorAfterInitialTouch } from 'features/event_type_editor'

import {
  DragHandle,
  addChoiceButtonCss,
  answerChoiceControlCss,
  answerChoiceFieldCss,
  answerChoiceRowCss,
  answerChoicesSectionCss,
} from './styles'

const getBlankAnswerChoice = () => ({
  id: nanoid(5),
  choice: '',
})

const MAX_ANSWERS_COUNT = {
  select: 300,
  choices_one: 200,
  choices_many: 200,
}

const generateAnswerChoices = () => ([...Array(3).keys()].map(() => getBlankAnswerChoice()))

const AnswerChoice = ({
  id,
  fieldName,
  deleteChoice,
  canDelete,
}) => {
  useErrorAfterInitialTouch(fieldName)

  const {
    sortable: {
      attributes,
      listeners,
      setNodeRef,
    },
    itemStyle,
  } = useAdvancedSortable({ id })

  return (
    <div
      ref={setNodeRef}
      className={answerChoiceRowCss}
      data-testid={`answer-choice-row-${id}`}
      {...itemStyle}
    >
      <DragHandle className={answerChoiceControlCss} {...attributes} {...listeners}>
        <UiIcon color={colorIconSecondary} icon={IconGripVertical} size="medium" title="" />
      </DragHandle>
      <TextField
        aria-label={fieldName}
        className={answerChoiceFieldCss}
        name={fieldName}
        placeholder={I18n.t('publisher.event_type_editor_crud.booking_page.choice_placeholder')}
      />
      {canDelete && (
        <span className={answerChoiceControlCss}>
          <IconButton
            appearance="ghost"
            aria-label={I18n.t('publisher.action.remove')}
            icon={IconTrash}
            size="small"
            title={I18n.t('publisher.action.remove')}
            onClick={() => deleteChoice(id)}
          />
        </span>
      )}
    </div>
  )
}

export const AnswerChoicesSection = ({ index }) => {
  const [{ value: format }] = useFormField({ name: `inviteeQuestions.${index}.format` })
  const [
    { value: answerChoices },
    { error: answerChoicesError },
    { setValue },
  ] = useFormField({ name: `inviteeQuestions.${index}.answerChoices` })

  const deleteChoice = (toDelete) => {
    setValue(answerChoices.filter(({ id }) => id !== toDelete))
  }

  const addNewChoice = () => {
    setValue([...answerChoices, getBlankAnswerChoice()])
  }

  useEffect(() => {
    if (!answerChoices) {
      setValue(generateAnswerChoices())
    }
  }, [answerChoices, setValue])

  if (!answerChoices) return null

  const handleReorderAnswers = (newOrder) => { setValue(newOrder) }

  const canAddNew = answerChoices.length < MAX_ANSWERS_COUNT[format]
  const shouldDisplayError = typeof answerChoicesError === 'string' || answerChoicesError instanceof String

  return (
    <div className={answerChoicesSectionCss} data-testid={`answer-choices-section-${index}`}>
      <VerticalSortableContext items={answerChoices} setItems={handleReorderAnswers}>
        {answerChoices.map(({ id }, choiceIndex) => (
          <AnswerChoice
            key={id}
            id={id}
            fieldName={`inviteeQuestions.${index}.answerChoices.${choiceIndex}.choice`}
            deleteChoice={deleteChoice}
            canDelete={answerChoices.length > 1}
          />
        ))}
      </VerticalSortableContext>
      {shouldDisplayError && <ErrorMessage message={answerChoicesError} />}
      <Button
        appearance="ghost"
        className={addChoiceButtonCss}
        data-testid={`add-choice-button-${index}`}
        disabled={!canAddNew}
        size="small"
        type="button"
        onClick={addNewChoice}
      >
        <UiIcon icon={IconPlus} size="small" color={colorIconStandard} title="" />
        {I18n.t('publisher.event_type_editor_crud.booking_page.add_choice')}
      </Button>
    </div>
  )
}
